import { useState } from "react";
import { navLinks } from "@/mocks/chigua";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daetqtbep053ijub1hh0";

const colFooter = [
  { label: "看热点", href: "#hot" },
  { label: "看速递", href: "#feed" },
  { label: "看深度", href: "#deep" },
  { label: "看盘点", href: "#weekly" },
];

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("idle");
    setMessage("");
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾字段
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      // 疑似机器人：不真正提交，但反馈与正常一致
      form.reset();
      setStatus("ok");
      setMessage("订阅成功，感谢关注吃瓜中心，最新情报会第一时间送达你的邮箱。");
      return;
    }

    // 空的反垃圾字段不入参
    if (formData.get("contact_alt") === "") formData.delete("contact_alt");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let code = "";
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code || parsed?.meta?.code || "";
      } catch {
        code = "";
      }
      const isSpam = /spam/i.test(responseText);
      if (res.ok && code === "OK" && !isSpam) {
        form.reset();
        setStatus("ok");
        setMessage("订阅成功，感谢关注吃瓜中心，最新情报会第一时间送达你的邮箱。");
      } else {
        setStatus("error");
        setMessage("抱歉，订阅未能完成，请稍后再试或直接通过邮箱与我们联系。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后重试。");
    }
  };

  return (
    <footer className="bg-background-900 text-background-100">
      <div className="chigua-container mx-auto pt-12 md:pt-16">
        {/* newsletter band */}
        <div className="grid grid-cols-1 items-center gap-6 rounded-[28px] bg-background-800 p-6 md:p-8 lg:grid-cols-2">
          <div>
            <h3 className="flex items-center gap-2 font-display text-xl text-background-50">
              <i className="ri-mail-send-line text-primary-300"></i> 每日情报订阅
            </h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-background-200">
              每天早上收一封「吃瓜简报」，热搜榜单 + 事件脉络一次掌握，不错过任何一颗好瓜。
            </p>
          </div>
          <form
            onSubmit={onSubmit}
            data-readdy-form="吃瓜中心情报订阅"
            className="flex flex-col gap-2 sm:flex-row sm:items-center"
            noValidate
          >
            {/* honeypot */}
            <input
              type="text"
              name="contact_alt"
              className="hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <label htmlFor="footer-email" className="sr-only">订阅邮箱</label>
            <input
              id="footer-email"
              type="email"
              name="email"
              required
              placeholder="请输入你的邮箱地址"
              className="w-full flex-1 rounded-full border border-background-600 bg-background-900 px-5 py-3 text-sm text-background-50 outline-none transition-colors placeholder:text-background-400 focus:border-primary-400 sm:w-auto"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
            >
              立即订阅
            </button>
          </form>
          {message && (
            <p
              role="status"
              className={`text-sm lg:col-span-2 ${
                status === "ok" ? "text-accent-300" : status === "error" ? "text-secondary-300" : ""
              }`}
            >
              {status === "ok" ? <i className="ri-checkbox-circle-fill mr-1"></i> : null}
              {status === "error" ? <i className="ri-error-warning-fill mr-1"></i> : null}
              {message}
            </p>
          )}
        </div>

        {/* link grid */}
        <div className="mt-10 grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <a href="#top" className="inline-flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-2xl bg-primary-500 text-background-50">
                <i className="ri-fire-fill"></i>
              </span>
              <span className="font-display text-lg text-background-50">吃瓜中心</span>
            </a>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-background-300">
              把瓜吃明白，把热点看清楚。每日全网热点事件脉络的情报整理站。
            </p>
            <div className="mt-4 flex items-center gap-2">
              {["weibo", "bilibili", "wechat", "douban"].map((s) => (
                <a
                  key={s}
                  href="#top"
                  aria-label={`吃瓜中心 ${s} 主页`}
                  className="grid h-9 w-9 place-items-center rounded-full bg-background-800 text-background-300 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={`text-base ${s === "weibo" ? "ri-weibo-fill" : s === "bilibili" ? "ri-play-circle-fill" : s === "wechat" ? "ri-chat-smile-3-fill" : "ri-bookmark-3-fill"}`}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50">栏目导航</h4>
            <ul className="mt-3 space-y-2 text-sm text-background-300">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="transition-colors hover:text-primary-300">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50">快速入口</h4>
            <ul className="mt-3 space-y-2 text-sm text-background-300">
              {colFooter.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="transition-colors hover:text-primary-300">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50">联系我们</h4>
            <ul className="mt-3 space-y-2 text-sm text-background-300">
              <li className="flex items-center gap-2"><i className="ri-mail-line text-primary-400"></i> hello@chigua-hub.example</li>
              <li className="flex items-center gap-2"><i className="ri-lightbulb-flash-line text-secondary-400"></i> 爆料投稿：tougao@chigua-hub.example</li>
              <li className="flex items-center gap-2"><i className="ri-chat-1-line text-accent-400"></i> 商务合作：bd@chigua-hub.example</li>
            </ul>
            <p className="mt-3 text-xs leading-relaxed text-background-400">
              本站为内容展示站，联系邮箱为示例用途，请勿发送真实敏感信息。
            </p>
          </div>
        </div>

        {/* bottom bar */}
        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-700 py-6 sm:flex-row">
          <p className="text-xs text-background-400">
            © {new Date().getFullYear()} 吃瓜中心 · 理性吃瓜，谨慎求证
          </p>
          <div className="flex items-center gap-4 text-xs text-background-400">
            <a href="#faq" className="transition-colors hover:text-background-100">吃瓜守则</a>
            <a href="#about" className="transition-colors hover:text-background-100">关于我们</a>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex items-center gap-1.5 rounded-full bg-background-800 px-3.5 py-1.5 text-background-200 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              <i className="ri-arrow-up-line"></i> 回到顶部
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}